import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import ApiClient from 'Api/ApiClient';
import AuthContext from 'context/AuthContext';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { UrlHelpers } from 'urlHelpers/urlHelper';

import Loader from 'components/Loader/Loader';

const STEPS = {
  FORM: 'form',
  ARE_YOU_SURE: 'areYouSure',
  RESULT: 'result',
};

const dialogPaperSx = {
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(16, 42, 67, 0.18)',
  p: 1,
  m: { xs: 2, sm: 4 },
  width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
  maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
};

const titleSx = {
  fontSize: { xs: '20px !important', sm: '24px !important' },
  fontWeight: 700,
  color: '#0F172A',
};

const actionsSx = {
  mt: { xs: 3, sm: 4 },
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  justifyContent: 'space-between',
  gap: 1.5,
};

const actionButtonWidth = { xs: '100%', sm: 'auto' };

const labelSx = {
  fontSize: '12px !important',
  fontWeight: 700,
  letterSpacing: '0.6px',
  color: '#5B7184',
  textTransform: 'uppercase',
};

const valueSx = {
  fontSize: { xs: '15px !important', sm: '17px !important' },
  fontWeight: 700,
  color: '#102A43',
  overflowWrap: 'anywhere',
};

function ScopeReminder() {
  return (
    <Typography sx={{ fontSize: '15px !important', color: '#334155', lineHeight: 1.6 }}>
      <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>
        Scope reminder:
      </Box>{' '}
      This clears the Admin Lock and Password Lock.
    </Typography>
  );
}

const apiClient = new ApiClient();

function UnlockUserDialog({ open, customer, onClose, onSuccess }) {
  const { initiateUiResponse } = useContext(AuthContext);
  const [step, setStep] = useState(STEPS.FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const accountStatus = customer?.accountStatus || {};
  const name = customer?.name || customer?.username || 'this customer';
  const shortName = customer?.username || name;
  const reasonRecorded = accountStatus.lockReason || accountStatus.reason || 'No data found';
  const appliedAt = accountStatus.lockedAt || accountStatus.appliedAt || 'No data found';

  const handleExited = () => {
    setStep(STEPS.FORM);
    setSubmitting(false);
    setSuccess(false);
    setErrorMessage('');
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleUnlock = async () => {
    setSubmitting(true);
    try {
      await apiClient.put(
        `${UrlHelpers.updateUser(customer?.preferredusernm || customer?.username)}?status=0`,
        {},
        {
          headers: {
            'x-api-key': initiateUiResponse.REACT_APP_X_API_KEY_ADMIN,
            appid: 'fis',
          },
        },
        initiateUiResponse?.REACT_APP_ADMIN_TOKEN
      );
      setSuccess(true);
      setErrorMessage('');
      onSuccess?.();
    } catch {
      setSuccess(false);
      setErrorMessage('We are unable to process your request. Please try again later.');
    } finally {
      setSubmitting(false);
      setStep(STEPS.RESULT);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: handleExited }}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Loader open={submitting} />
      {step === STEPS.FORM && (
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography sx={titleSx}>Unlock user</Typography>
          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
              p: { xs: 2, sm: 2.5 },
              borderRadius: '10px',
              backgroundColor: '#F5F7FB',
              border: '1px solid #E4E8ED',
            }}
          >
            <ScopeReminder />
          </Box>

          <Stack spacing={2.5} sx={{ mt: 3 }}>
            <Box>
              <Typography sx={labelSx}>Reason recorded at lock</Typography>
              <Typography sx={valueSx}>{reasonRecorded}</Typography>
            </Box>
            <Box>
              <Typography sx={labelSx}>Applied at</Typography>
              <Typography sx={valueSx}>{appliedAt}</Typography>
            </Box>
          </Stack>

          <Box sx={actionsSx}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disableRipple
              sx={{
                width: actionButtonWidth,
                minWidth: 100,
                height: 44,
                borderRadius: '8px',
                background: '#ffffff',
                color: '#000000',
                textTransform: 'none',
                border: '1px solid #000000',
                fontWeight: 600,
                boxShadow: 'none',
                fontSize: '15px !important',
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: '#474747',
                  boxShadow: 'none',
                  border: 'none',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleUnlock}
              variant="contained"
              disableRipple
              sx={{
                width: actionButtonWidth,
                height: 44,
                px: 3,
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#000000',
                border: '1px solid #000000',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px !important',
                boxShadow: 'none',

                '&:hover': {
                  backgroundColor: '#ffffff',
                  boxShadow: 'none',
                  border: '1px solid #000000',
                  color: '#000000',
                },
              }}
            >
              Unlock user
            </Button>
          </Box>
        </DialogContent>
      )}

      {step === STEPS.RESULT && (
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: success ? '#DEF3E4' : '#FDECEC',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
            }}
          >
            {success ? (
              <CheckIcon sx={{ color: '#1B9E4B', fontSize: 34 }} />
            ) : (
              <CloseIcon sx={{ color: '#D14343', fontSize: 34 }} />
            )}
          </Box>

          {success && <Typography sx={{ ...titleSx, mt: 2 }}>Admin Lock cleared</Typography>}
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: '15px !important', sm: '17px !important' },
              overflowWrap: 'anywhere',
              color: success ? '#475569' : '#d71714',
            }}
          >
            {success
              ? `The Admin Lock on ${shortName} account has been removed.`
              : errorMessage || `We are unable to process your request. Please try again later.`}
          </Typography>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={handleClose}
              variant="contained"
              disableRipple
              sx={{
                minWidth: 120,
                height: 44,
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#6B21D8',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px !important',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#5A18BC', boxShadow: 'none' },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

UnlockUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  customer: PropTypes.shape({
    name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    registeredfrom: PropTypes.string,
    accountStatus: PropTypes.object,
    preferredusernm: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

UnlockUserDialog.defaultProps = {
  customer: null,
  onSuccess: null,
};

export default UnlockUserDialog;
